import { useState, useEffect } from "react";
import { Heart, Trash2, Check } from "lucide-react";
import { toast } from "react-toastify";
import CharacterSidebar from "./CharacterSidebar";
import ConfirmModal from "../../components/ConfirmModal";
import request from "../../utils/request";
import placeholderImage from "../../assets/character.png";

export default function LibraryTab({
  searchQuery,
  onSearchChange,
  selectedCustomCharacter,
  selectedTemplateCharacter,
  onSelectedCustomCharacterChange,
  onSelectedTemplateCharacterChange,
}) {
  const [allCharacters, setAllCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favoritedCharacters, setFavoritedCharacters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    gender: [],
    ageCategory: [],
  });

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null); // Used for single delete modal message
  const [deleteIds, setDeleteIds] = useState([]); // IDs to be deleted (single or multiple)
  const [isDeleting, setIsDeleting] = useState(false);

  // Selection state for multi-delete
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);

  // ─── API ────────────────────────────────────────────────────────────
  const getCharacters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (selectedFilters.gender.length > 0)
        params.append("gender", selectedFilters.gender.join(","));
      if (selectedFilters.ageCategory.length > 0)
        params.append("ageCategory", selectedFilters.ageCategory.join(","));

      const response = await request.get(`/characters/public?${params.toString()}`);
      setAllCharacters(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching characters:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (character, e) => {
    e.stopPropagation();
    setCharacterToDelete(character);
    setDeleteIds([character._id]);
    setDeleteModalOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setDeleteIds(selectedCharacterIds);
    setCharacterToDelete(null); // Generic message for bulk delete
    setDeleteModalOpen(true);
  };

  const handleToggleSelect = (characterId, e) => {
    e.stopPropagation();
    setSelectedCharacterIds((prev) =>
      prev.includes(characterId)
        ? prev.filter((id) => id !== characterId)
        : [...prev, characterId]
    );
  };

  const handleConfirmDelete = async () => {
    if (deleteIds.length === 0) return;
    setIsDeleting(true);
    try {
      await request.delete("/characters/admin", {
        data: { characterIds: deleteIds },
      });

      // Clear selection if deleted characters were selected
      if (deleteIds.includes(selectedCustomCharacter)) {
        onSelectedCustomCharacterChange(null);
      }
      if (deleteIds.includes(selectedTemplateCharacter)) {
        onSelectedTemplateCharacterChange(null);
      }

      // Remove from favorites if they were favorited
      setFavoritedCharacters((prev) =>
        prev.filter((id) => !deleteIds.includes(id))
      );

      // Clear multi-selection
      setSelectedCharacterIds((prev) =>
        prev.filter((id) => !deleteIds.includes(id))
      );

      // Refresh the list
      await getCharacters();

      toast.success(
        deleteIds.length > 1
          ? "Characters deleted successfully"
          : "Character deleted successfully",
        { position: "bottom-right" }
      );
    } catch (error) {
      console.error("Error deleting characters:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete characters",
        { position: "bottom-right" }
      );
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setCharacterToDelete(null);
      setDeleteIds([]);
    }
  };

  // ─── Filters ─────────────────────────────────────────────────────────
  const toggleFilter = (category, value) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((v) => v !== value)
        : [...prev[category], value],
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({ gender: [], ageCategory: [] });
  };

  const hasActiveFilters = Object.values(selectedFilters).some(
    (arr) => arr.length > 0
  );

  // ─── Favorites ───────────────────────────────────────────────────────
  const toggleFavorite = (characterId, e) => {
    e.stopPropagation();
    setFavoritedCharacters((prev) =>
      prev.includes(characterId)
        ? prev.filter((id) => id !== characterId)
        : [...prev, characterId]
    );
  };

  // ─── Selection ───────────────────────────────────────────────────────
  const handleCharacterClick = (character) => {
    const isCustom =
      character.type === "ai_generated" || character.type === "custom";
    const isSelected = isCustom
      ? selectedCustomCharacter === character._id
      : selectedTemplateCharacter === character._id;

    if (isCustom) {
      onSelectedCustomCharacterChange(isSelected ? null : character._id);
      onSelectedTemplateCharacterChange(null);
    } else {
      onSelectedTemplateCharacterChange(isSelected ? null : character._id);
      onSelectedCustomCharacterChange(null);
    }
  };

  // ─── Client-side derived list ─────────────────────────────────────────
  const displayedCharacters = allCharacters.filter((character) => {
    // Search query: match name, description, or age attribute
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchesQuery = (value) =>
        String(value ?? "").toLowerCase().includes(q);
      const nameMatch = matchesQuery(character.name);
      const descMatch = matchesQuery(character.description);
      const ageMatch = matchesQuery(character.attributes?.age);
      if (!nameMatch && !descMatch && !ageMatch) return false;
    }
    return true;
  });

  // ─── Effects ──────────────────────────────────────────────────────────
  useEffect(() => {
    getCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem("favoritedCharacters");
    if (saved) {
      try {
        setFavoritedCharacters(JSON.parse(saved));
      } catch (_) { }
    }
  }, []);

  // Persist favorites to localStorage
  useEffect(() => {
    localStorage.setItem(
      "favoritedCharacters",
      JSON.stringify(favoritedCharacters)
    );
  }, [favoritedCharacters]);

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <CharacterSidebar
        selectedFilters={selectedFilters}
        onFilterChange={toggleFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Main Content Column */}
      <div className="flex-1 min-w-0">
        {/* Bulk Actions Bar */}
        {selectedCharacterIds.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center justify-between animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-red-600">
                {selectedCharacterIds.length} character
                {selectedCharacterIds.length > 1 ? "s" : ""} selected
              </span>
            </div>
            <button
              onClick={handleBulkDeleteClick}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
            >
              <Trash2 className="h-4 w-4" />
              Delete Selected
            </button>
          </div>
        )}

        {/* Character Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : (
          <CharacterGrid
            characters={displayedCharacters}
            selectedCustomCharacter={selectedCustomCharacter}
            selectedTemplateCharacter={selectedTemplateCharacter}
            favoritedCharacters={favoritedCharacters}
            selectedCharacterIds={selectedCharacterIds}
            onCharacterClick={handleCharacterClick}
            onToggleFavorite={toggleFavorite}
            onDeleteClick={handleDeleteClick}
            onToggleSelect={handleToggleSelect}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setCharacterToDelete(null);
          }
        }}
        title={deleteIds.length > 1 ? "Delete Characters" : "Delete Character"}
        message={
          deleteIds.length > 1
            ? `Are you sure you want to delete ${deleteIds.length} selected characters? This action cannot be undone.`
            : `Are you sure you want to delete "${characterToDelete?.name}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setCharacterToDelete(null);
          }
        }}
        isDeleting={isDeleting}
      />
    </div>
  );
}

// ─── CharacterGrid ────────────────────────────────────────────────────────────
function CharacterGrid({
  characters,
  selectedCustomCharacter,
  selectedTemplateCharacter,
  favoritedCharacters,
  selectedCharacterIds,
  onCharacterClick,
  onToggleFavorite,
  onDeleteClick,
  onToggleSelect,
}) {
  if (characters.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <i
          data-lucide="user-circle"
          className="h-16 w-16 mx-auto mb-4 opacity-50"
        ></i>
        <p className="text-lg font-medium mb-2">No characters found</p>
        <p className="text-sm">Try adjusting your filters or search query</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-8 gap-4">
      {characters.map((character) => {
        const isCustom =
          character.type === "ai_generated" || character.type === "custom";
        const isSelected = isCustom
          ? selectedCustomCharacter === character._id
          : selectedTemplateCharacter === character._id;
        const isMultiSelected = selectedCharacterIds.includes(character._id);
        const isFavorited = favoritedCharacters.includes(character._id);
        const imageUrl =
          character.baseImage?.originalUrl ||
          character.metadata?.baseImageUrl ||
          placeholderImage;

        return (
          <div
            key={character._id}
            onClick={() => onCharacterClick(character)}
            className={`group rounded-xl border overflow-hidden cursor-pointer transition-all hover:scale-105 hover:z-10 relative ${isSelected || isMultiSelected
              ? "ring-2 ring-orange-500 border-orange-500 shadow-xl"
              : "border-gray-200 bg-white hover:border-orange-300 hover:shadow-xl"
              }`}
          >
            <div className="relative aspect-[9/16] overflow-hidden">
              <img
                src={imageUrl}
                alt={character.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />

              {/* Selection Checkbox - Only for custom characters */}
              {isCustom && (
                <div
                  onClick={(e) => onToggleSelect(character._id, e)}
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-md flex items-center justify-center transition-all border-2 ${isMultiSelected
                    ? "bg-orange-500 border-orange-500 text-white"
                    : "bg-white/20 border-white/50 backdrop-blur-sm group-hover:opacity-100 opacity-0"
                    }`}
                >
                  {isMultiSelected && <Check className="h-4 w-4" />}
                </div>
              )}


              {/* Delete button — only for custom characters */}
              {isCustom && (
                <button
                  onClick={(e) => onDeleteClick(character, e)}
                  className="absolute top-2 right-2 z-10 w-7 h-7 rounded-lg flex items-center justify-center transition-all backdrop-blur-sm bg-red-500/80 text-white hover:bg-red-500 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="h-3 w-3 text-white" />
                </button>
              )}

              {/* Name overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <h4 className="text-sm font-semibold truncate text-white">
                  {character.name}
                </h4>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
